/** @format */
import { Thumbnail } from '@mpb-com/feather/components/atoms/thumbnail';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { CloudflareImageSize } from '@toucan/client/global/cloudflare';
import { fallbackImagePath } from '@toucan/client/global/product';

import { CardBackground } from '@toucan/client/shared/components/card-background';

import { CloudflareImage } from '@toucan/client/blocks/components/cloudflare-image';

import { truncateTagline } from '../../helpers';
import { DEFAULT_TEST_ID } from './config/constants';
import { FanCardProps, StyledFanCardProps } from './config/types';
import { styles } from './fan-card.styles';

export * from './config/constants';
export type { FanCardProps } from './config/types';

const StyledFanCard = styled.li<StyledFanCardProps>`
    ${styles}
`;

export const FanCard: FunctionComponent<FanCardProps> = ({
    model,
    angle,
    isSelected,
    onSelect,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    ...otherProps
}) => {
    const { name, imageUrl, shortDescription, id } = model;

    const handleClick = () => onSelect(id);

    return (
        <StyledFanCard $angle={angle} $isSelected={isSelected} {...otherProps}>
            <button
                type="button"
                className="fan-card-button"
                // aria-pressed (not aria-selected) is what makes this read as
                // a toggle to a screen reader, since it selects a card rather
                // than navigating anywhere.
                aria-pressed={isSelected}
                onClick={handleClick}
                data-testid={dataTestId}
            >
                <CardBackground
                    className="fan-card-surface"
                    // Switched off while selected so the hover growth does
                    // not fight the selected ring.
                    expandOnHover={!isSelected}
                >
                    <span className="fan-card-content">
                        {/*
                         * The box is pinned to an explicit width and height
                         * rather than an aspect ratio, so a tall, wide or
                         * square model image all leave the card exactly the
                         * same size; the image is contained inside it.
                         */}
                        <Thumbnail
                            className="fan-card-thumbnail"
                            width="100%"
                            height="var(--journey-fan-card__image-height)"
                        >
                            <CloudflareImage
                                // Remounted per model, so a card that changes
                                // which model it shows changes its image with
                                // it. @see timeline-detail/index.tsx
                                key={imageUrl}
                                src={imageUrl ?? fallbackImagePath}
                                fallbackSrc={fallbackImagePath}
                                // The name sits right next to the image, so
                                // alt text would only repeat it.
                                alt=""
                                imageSize={CloudflareImageSize.THUMBNAIL}
                            />
                        </Thumbnail>
                        <span className="fan-card-name">{name}</span>
                        {shortDescription && (
                            <span
                                className="fan-card-description"
                                data-testid={`${dataTestId}__description`}
                            >
                                {truncateTagline(shortDescription)}
                            </span>
                        )}
                    </span>
                </CardBackground>
            </button>
        </StyledFanCard>
    );
};
